// SSE parser for aiassist Buddy `/v1/aiassist/buddy/chat`. The server emits frames
// in the standard `event: <name>\ndata: <json>\n\n` shape via utils/sse.py, where
// the JSON payload is the full BuddyStreamEvent ({type, data}).

export type BuddyEvent =
  | { type: 'start'; data: Record<string, never> }
  | { type: 'token'; data: { text: string } }
  | { type: 'message'; data: { text: string } }
  | { type: 'narration'; data: { text: string } }
  // `args` and `preview` are optional because aiassist stopped sending them: the
  // call arguments and the tool's raw output were reaching the customer's browser
  // for a UI that reads neither. Kept in the type so a chat served by an older
  // aiassist during a deploy still parses.
  | { type: 'tool_call'; data: { name: string; args?: Record<string, unknown> } }
  | {
      type: 'tool_output'
      data: {
        name: string
        preview?: string
        success?: boolean
        status?: string
        // tool-specific extras (escalation_id, country, etc.)
        [k: string]: unknown
      }
    }
  | { type: 'sources'; data: { sources: Array<{ url: string; title?: string }> } }
  | { type: 'final'; data: { answer?: string; latency_ms?: number } }
  | { type: 'error'; data: { error: string } }
  // PAI stream contract (customer scope): the payload is the bare object, not the
  // {type, data} wrapper. `result` is the complete sanitized answer (no deltas).
  | { type: 'session'; data: { session_id: string } }
  | { type: 'result'; data: { text: string } }
  | { type: 'cost'; data: { total_cost_usd: number } }
  | { type: 'done'; data: Record<string, never> }

const parseFrame = (raw: string): BuddyEvent | null => {
  let eventName = ''
  const dataParts: string[] = []
  for (const line of raw.split('\n')) {
    if (line.startsWith('event:')) eventName = line.slice(6).trim()
    else if (line.startsWith('data:')) dataParts.push(line.slice(5).trim())
  }
  if (!eventName || dataParts.length === 0) return null
  try {
    const parsed = JSON.parse(dataParts.join('\n')) as { type?: string; data?: unknown }
    const type = (parsed.type ?? eventName) as BuddyEvent['type']
    // Legacy Buddy frames wrap the payload as {type, data}; PAI-contract frames
    // (session/result/cost/done) send the bare object. Prefer `data` when the
    // wrapper is present, else treat the parsed object itself as the payload.
    const data = (parsed.data ?? parsed) as never
    return { type, data } as BuddyEvent
  } catch {
    return null
  }
}

export async function* parseBuddyStream(
  response: Response,
  signal?: AbortSignal,
): AsyncGenerator<BuddyEvent> {
  if (!response.body) throw new Error('Buddy response has no body')
  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buf = ''
  try {
    while (true) {
      if (signal?.aborted) return
      const { value, done } = await reader.read()
      if (done) break
      buf += decoder.decode(value, { stream: true })
      // SSE frames are separated by a blank line (\n\n).
      let idx = buf.indexOf('\n\n')
      while (idx !== -1) {
        const raw = buf.slice(0, idx)
        buf = buf.slice(idx + 2)
        const frame = parseFrame(raw)
        if (frame) yield frame
        idx = buf.indexOf('\n\n')
      }
    }
    // End-of-stream: flush any bytes the decoder buffered while waiting for the
    // rest of a multi-byte UTF-8 sequence. Without this final no-arg decode,
    // a frame whose last codepoint straddled the chunk boundary would render
    // with a replacement char or be silently dropped.
    buf += decoder.decode()
    // Flush any trailing frame missing the terminator.
    if (buf.trim()) {
      const frame = parseFrame(buf)
      if (frame) yield frame
    }
  } finally {
    reader.releaseLock?.()
  }
}
